/**
 * Messaging library — types + WhatsApp template model (Greg's 2026-07-07
 * channel-editor work). The MessageItem shape carries the two-status lifecycle
 * (draft / live) plus per-channel content, a structured
 * WhatsApp template, and conversational quick replies.
 *
 * #635 seed-guard (2026-07-08 merge): the demo seed rows were removed — the
 * array below is EMPTY in app source. Rich message fixtures (subject /
 * perChannel / audience / whatsapp) are the mock layer's to serve via
 * listBuilderMessages (see CLAUDE-CODE-REQUESTS.md REQ-017).
 */
export type WaCategory = "utility" | "marketing" | "authentication";
export type WaHeaderType = "none" | "text" | "image" | "video" | "document";
export type WaButtonType = "quick_reply" | "url" | "call";
export type WaStatus = "draft" | "in_review" | "active" | "rejected";
export interface WaButton { id: string; type: WaButtonType; label: string; value?: string; }
export interface WaTemplate {
  category: WaCategory;
  headerType: WaHeaderType;
  headerText: string;
  footer: string;
  buttons: WaButton[];
  status: WaStatus;
}
export const WA_DEFAULT: WaTemplate = { category: "marketing", headerType: "none", headerText: "", footer: "", buttons: [], status: "draft" };

/** REQ-112g — the WYSIWYG email design (messaging overhaul step 2). */
import type { EmailDesign } from "./email/email-model";
/** REQ-116 — the calendar-invite template (2026-07-31). */
import type { IcsTemplate } from "./calendar-ics";
import type { WalletTemplate } from "./wallet-pass";

export interface MessageItem {
  id: string;
  name: string;
  channels: string[];
  /** Two-status lifecycle (operator, 2026-08-04): draft or live — legacy values are
   *  normalized to live at the read seam (useMessaging.normStatus). */
  status: "draft" | "live";
  audience: string;
  updated: string;
  subject: string;
  perChannel: Record<string, string>;
  body?: string;
  sentLabel?: string;
  /** WhatsApp requires a structured, Meta-approved template (not free text). */
  whatsapp?: WaTemplate;
  /** Conversational-channel quick replies (Messenger / Instagram), by channel id.
   *  Those channels were retired from the BUILDER on 2026-07-30 — the field stays
   *  so existing rows round-trip untouched. */
  quickReplies?: Record<string, string[]>;
  /** REQ-112 — multilingual content. The root subject/perChannel/whatsapp fields
   *  hold the DEFAULT language; `translations` holds every other language keyed
   *  by BCP-47 tag. `languages` always includes `defaultLanguage`. Both absent on
   *  legacy rows — treated as single-language with default "en". */
  languages?: string[];
  defaultLanguage?: string;
  translations?: Record<string, MessageTranslation>;
  /** REQ-112g — WYSIWYG email design for the DEFAULT language (step 2). The
   *  generated plain-text alternative is mirrored into perChannel.email. */
  emailDesign?: EmailDesign;
  /** REQ-112h — step 3: when set, this message's email is CUSTOM HTML and this
   *  string (not the design) is the send source. The design is kept aside so
   *  "Rebuild in WYSIWYG" can restore it. */
  emailHtml?: string;
  /** REQ-116 — calendar-invite template for the DEFAULT language. */
  ics?: IcsTemplate;
  /** REQ-117 — wallet-pass template for the DEFAULT language. */
  wallet?: WalletTemplate;
}

/** One language's content for a message (REQ-112). Anything absent falls back
 *  to the default language at send time (operator decision 2026-07-30). */
export interface MessageTranslation {
  subject?: string;
  perChannel?: Record<string, string>;
  /** Meta reviews WhatsApp templates PER LANGUAGE — each language carries its
   *  own template, including its own approval status. */
  whatsapp?: WaTemplate;
  /** REQ-112g — this language's email design (explicit copy-from-default). */
  emailDesign?: EmailDesign;
  /** REQ-112h — this language's custom-HTML source, when code-sourced. */
  emailHtml?: string;
  /** REQ-116 — this language's calendar-invite template (summary/description are
   *  customer-facing copy, so they translate like everything else). */
  ics?: IcsTemplate;
  /** REQ-117 — this language's wallet-pass template. */
  wallet?: WalletTemplate;
}

// #635: baked message library removed — served via listBuilderMessages /
// @v0y4ge/api-mocks (REQ-001 resolved; content-field enrichment = REQ-017).
